import React, { useState } from 'react';
import { Box, Grid } from '@mui/material';
import styled from '@emotion/styled';
import {
  closestCorners,
  defaultDropAnimation,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import PolarisIcon from '../../PolarisIcon';
import { FilterLayoutItemDraggableData } from '@/components/_common/Filters/FilterLayoutsModal/FilterLayoutItemDraggable';
import FilterLayoutColumn from './FilterLayoutColumn';
import FilterLayoutItem from '@/components/_common/Filters/FilterLayoutsModal/FilterLayoutItem';
import { LayoutModalItems } from '@/components/_common/Filters/FilterLayoutsModal/types';

interface Props {
  items: LayoutModalItems;
  setItems: (items: LayoutModalItems) => void;
}

const FilterLayoutColumns: React.FC<Props> = ({ items, setItems }) => {
  const [activeDrag, setActiveDrag] = useState<FilterLayoutItemDraggableData | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const reOrderFilterItems = (source: FilterLayoutItemDraggableData, target: { columnName: 'show' | 'hide'; index?: number } | undefined) => {
    if (!target) return;
    const newItems = structuredClone(items);
    const [updated] = newItems[source.columnName].splice(source.index, 1);
    updated.is_hidden = target.columnName === 'hide';

    newItems[target.columnName].splice(
      typeof target.index === 'number'
        ? // when `index` exists, we are dragging over another filter item in the column. In that case insert an item in the place of `target`.
          target.index
        : // when `index` is undefined, this means we're dragging directly into the column. In that insert an item at the end of the list.
          items[target.columnName].length,
      0,
      updated
    );
    setItems(newItems);
  };

  const onDragStart = (event: DragStartEvent) => {
    setActiveDrag(event.active.data.current as FilterLayoutItemDraggableData);
  };

  const onDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    reOrderFilterItems(active.data.current as FilterLayoutItemDraggableData, over?.data.current as FilterLayoutItemDraggableData);
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    reOrderFilterItems(active.data.current as FilterLayoutItemDraggableData, over?.data.current as FilterLayoutItemDraggableData);
    setActiveDrag(null);
  };

  const togglePin = (columnName: 'show' | 'hide', id: string) => {
    const newItems = structuredClone(items);
    const targetItem = newItems[columnName].find((item) => item.id === id);
    if (targetItem) {
      targetItem.is_quick = !targetItem.is_quick;
      setItems(newItems);
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={onDragStart} onDragOver={onDragOver} onDragEnd={onDragEnd}>
      <Grid container>
        <FilterLayoutColumn columnName="show" items={items.show} togglePin={togglePin} />
        <Grid item pt={5} display="flex" alignItems="center">
          <SwapIcon>
            <PolarisIcon icon="arrow_bidirectional_left_right" width={20} height={20} style={{ display: 'block' }} />
          </SwapIcon>
        </Grid>
        <FilterLayoutColumn columnName="hide" items={items.hide} togglePin={togglePin} />
      </Grid>

      <DragOverlay dropAnimation={defaultDropAnimation}>{activeDrag ? <FilterLayoutItem item={activeDrag.item} /> : null}</DragOverlay>
    </DndContext>
  );
};

export default FilterLayoutColumns;

const SwapIcon = styled(Box)(({ theme }) => ({
  margin: theme.spacing(0, 1.5),
  padding: '6px',
  borderRadius: '50%',
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.background.default
}));
